/**
 * 読み込み中・エラー表示モジュール
 */
// 外部モジュールをインポート
import React, { useState, useRef, useEffect } from "react";

// 描画コンポーネントをインポート
import {Spinner, Alert} from "react-bootstrap";


/**
 * 読み込み中表示を描画
 * @return ReactElement
 */
function LoadingSpinnerRenderrender(
  {
    showSpinner,
    className,
    height,
    backgroundColor,
  }:
  {
    showSpinner: boolean,
    className?: string,
    height: React.CSSProperties["height"],
    backgroundColor: React.CSSProperties["backgroundColor"],
  }
) {
  if (showSpinner) {
    return (
      <div 
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: "1199",
          width: "100%",
          height: height,
          backgroundColor: backgroundColor,
        }}
        className={className}
      >
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </Spinner>
      </div>
    );
  }

  return null;
}


/**
 * エラーを描画
 * @return ReactElement
 */
function LoadingErrorRender(
  {
    errorMsg,
  }:
  {
    errorMsg: React.ReactNode,
  }
) {
  return (
    <Alert variant="warning" className="m-0">
      {errorMsg}
    </Alert>
  );
}


function childIsfunction(obj: unknown): obj is () => React.ReactNode {
  return typeof obj === "function";
}


interface LoadingProps {
  spinnerStart?: number;
  isLoading?: boolean;
  isLoadError?: boolean;
  isSendError?: boolean;
  errorMsg?: React.ReactNode;
  className?: string;
  height?: React.CSSProperties["height"];
  backgroundColor?: React.CSSProperties["backgroundColor"];
  children?: React.ReactNode | (() => React.ReactNode);
}

export default function Loading({
  children,
  spinnerStart=0,
  isLoading=false,
  isLoadError=false,
  isSendError=false,
  errorMsg="",
  className,
  height="auto",
  backgroundColor="rgba(255, 255, 255, 1)",
}: LoadingProps) {
  const [showSpinner, setShowSpinner] = useState(false);
  const spinnerTimerID = useRef(-1);

  useEffect(() => {
    // TimerID
    spinnerTimerID.current = -1;

    // Start Spinner Timer
    if (spinnerStart !== 0) {
      // タイムアウト時間経過後から読み込み中スピナーを表示
      spinnerTimerID.current = window.setTimeout(() => {
        setShowSpinner(true);
        spinnerTimerID.current = -1;
      }, spinnerStart);

    } else {
      setShowSpinner(true);
    }

    return () => {
      if (spinnerTimerID.current !== -1) {
        window.clearTimeout(spinnerTimerID.current);
        spinnerTimerID.current = -1;
      }
    };
  }, [spinnerStart, setShowSpinner]);

  // 読み込みエラー・・・childrenを描画せずエラー表示
  if (isLoadError) {
    return <LoadingErrorRender errorMsg={errorMsg} />;
  
  // 読み込み中・・・スピナー表示 (時間経過までは空白表示)
  } else if (isLoading) {
    return (
      <LoadingSpinnerRenderrender
        showSpinner={showSpinner}
        className={className}
        height={height}
        backgroundColor={backgroundColor}
      />
    );

  // 読み込みエラー・読み込み中表示なし・・・何もしない
  } else {
    /* nothing to do */
  }

  return (
    <>
      { // 送信エラー・・・エラー表示 + children表示
        (isSendError === true) && 
        <LoadingErrorRender errorMsg={errorMsg} />
      }
      { children ?
          childIsfunction(children) ?
            children()
          :
            children
        :
          null
      }
    </>
  );
}
